import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useMembership } from '../../contexts/MembershipContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import MembershipCard from '../../components/membership/MembershipCard';
import MembershipStatus from '../../components/membership/MembershipStatus';
import PaymentQRCode from '../../components/payment/PaymentQRCode';
import PaymentStatusChecker from '../../components/payment/PaymentStatusChecker';
import * as FiIcons from 'react-icons/fi';

const { FiCreditCard, FiBookOpen, FiFileText, FiHelpCircle, FiStar, FiInfo, FiArrowRight } = FiIcons;

const MembershipPlans = () => {
  const { user } = useAuth();
  const { 
    membershipPlans, 
    loading, 
    subscribeToPlan, 
    getMembershipDetails
  } = useMembership();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeMembership, setActiveMembership] = useState(null);
  const [paidPlanId, setPaidPlanId] = useState(null);
  const [lastTxnId, setLastTxnId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('qr');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  useEffect(() => {
    // Get current membership details if any
    const membershipDetails = getMembershipDetails();
    setActiveMembership(membershipDetails);
  }, [getMembershipDetails]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleSubscribe = async (planArg) => {
    const planToUse = planArg || selectedPlan;
    if (!planToUse) {
      toast.error('Please select a plan');
      return;
    }
    // Initialize payment UI for the selected plan
    setProcessingPayment(false);
    setPaymentConfirmed(false);
    setPaidPlanId(null);
    setLastTxnId(null);
    setPaymentMethod('qr');
    toast('Choose a payment method below to continue');
  };

  const handlePaymentSuccess = () => {
    // Confirm payment and enable activation
    const txn = `txn_${Date.now()}`;
    setPaymentConfirmed(true);
    setPaidPlanId((selectedPlan || {}).id);
    setLastTxnId(txn);
    toast.success('Payment successful. Click Activate to start your membership.');
  };

  const handleManualPayment = async () => {
    try {
      setProcessingPayment(true);
      // In manual mode, user confirms externally then clicks this
      const txn = `txn_${Date.now()}`;
      setPaymentConfirmed(true);
      setPaidPlanId((selectedPlan || {}).id);
      setLastTxnId(txn);
      toast.success('Payment marked completed. Click Activate to start your membership.');
    } catch (e) {
      toast.error('Failed to confirm payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleActivate = async () => {
    if (!paidPlanId) {
      toast.error('Please pay for a plan first');
      return;
    }
    const plan = membershipPlans.find(p => p.id === paidPlanId);
    if (!plan) {
      toast.error('Selected plan not found');
      return;
    }
    try {
      const paymentDetails = {
        amount: plan.price,
        paymentMethod: 'credit_card',
        transactionId: lastTxnId || `txn_${Date.now()}`
      };
      await subscribeToPlan(plan.id, paymentDetails);
      const updatedMembership = getMembershipDetails();
      setActiveMembership(updatedMembership);
      setPaidPlanId(null);
      setLastTxnId(null);
    } catch (e) {
      toast.error('Activation failed. Please try again.');
    }
  };

  const benefits = [
    {
      icon: FiStar,
      title: 'No Service Charges',
      description: 'Pay only the application fee with zero service charges for all applications'
    },
    {
      icon: FiBookOpen,
      title: 'Study Materials',
      description: 'Get unlimited access to our comprehensive study materials library'
    },
    {
      icon: FiFileText,
      title: 'Previous Papers',
      description: 'Access previous years\' question papers for better preparation'
    },
    {
      icon: FiHelpCircle,
      title: 'Expert Support',
      description: 'Get personalized guidance from our expert counselors'
    }
  ];

  // Check if user has an active membership
  const isCurrentPlan = (plan) => {
    if (!activeMembership) return false;
    return activeMembership.plan.id === plan.id;
  };
  
  // Check if membership has expired
  const isMembershipExpired = () => {
    if (!activeMembership) return false;
    const now = new Date();
    const endDate = new Date(activeMembership.endDate);
    return now > endDate;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-bold text-gray-900">Membership Plans</h1>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            Save money with our membership plans. Pay only application fees, no service charges, and get exclusive benefits.
          </p>
        </motion.div>

        {/* Current Membership Status (if active) */}
        {activeMembership && !isMembershipExpired() && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <MembershipStatus membershipDetails={activeMembership} />
          </motion.div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {loading ? (
            <div className="col-span-3 text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading membership plans...</p>
            </div>
          ) : (
            membershipPlans.map((plan, index) => (
              <MembershipCard
                key={plan.id}
                plan={plan}
                isSelected={selectedPlan?.id === plan.id}
                onSelect={handlePlanSelect}
                isActive={false}
                isExpired={isMembershipExpired() && isCurrentPlan(plan)}
                currentPlan={isCurrentPlan(plan) && !isMembershipExpired()}
                onPay={handleSubscribe}
                showPay={true}
                activatingId={processingPayment ? (selectedPlan?.id || plan.id) : null}
              />
            ))
          )}
        </div>

        {/* Subscription Section */}
        {selectedPlan && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.5 }}
            className="mb-16"
          >
            <Card className="p-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Subscribe to {selectedPlan.name}
                  </h2>
                  <p className="text-gray-600">
                    {selectedPlan.description}. Valid for {Math.floor(selectedPlan.duration_days / 30)} months.
                  </p>
                </div>
                <div className="mt-4 md:mt-0">
                  <Button
                    onClick={() => handleSubscribe(selectedPlan)}
                    className="flex items-center space-x-2"
                    size="lg"
                  >
                    <SafeIcon icon={FiCreditCard} className="w-5 h-5" />
                    <span>Proceed to Pay ₹{selectedPlan.price}</span>
                  </Button>
                </div>
              </div>

              {/* Payment Methods (shown after clicking proceed) */}
              {(paymentMethod && selectedPlan) && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Pay via UPI</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPaymentMethod('qr')}
                        className={`px-3 py-1 text-sm rounded-full ${paymentMethod === 'qr' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        QR Code
                      </button>
                      <button
                        onClick={() => setPaymentMethod('manual')}
                        className={`px-3 py-1 text-sm rounded-full ${paymentMethod === 'manual' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        Manual Entry
                      </button>
                    </div>
                  </div>

                  {paymentMethod === 'qr' ? (
                    <>
                      <PaymentQRCode
                        order={{
                          orderId: `MEM${Date.now().toString().slice(-8)}`,
                          serviceName: `Membership: ${selectedPlan.name}`,
                          totalAmount: selectedPlan.price,
                        }}
                        onPaymentSuccess={handlePaymentSuccess}
                      />
                      {!paymentConfirmed && (
                        <PaymentStatusChecker
                          orderId={`MEM${Date.now().toString().slice(-8)}`}
                          onPaymentConfirmed={handlePaymentSuccess}
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center mb-2">
                      <div className="bg-gray-100 p-4 rounded-lg mb-4 w-full text-center">
                        <h3 className="font-medium text-gray-900 mb-2">UPI Payment Details</h3>
                        <p className="text-gray-700 font-medium">UPI ID: how2apply@ybl</p>
                        <p className="text-sm text-gray-600 mt-2">Amount: ₹{selectedPlan.price}</p>
                        <p className="text-xs text-gray-500 mt-1">Reference: Will be generated</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg w-full">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <SafeIcon icon={FiInfo} className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-blue-700">
                              After paying via your UPI app, click the button below to confirm.
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={handleManualPayment}
                        loading={processingPayment}
                        className="w-full mt-6 flex items-center justify-center"
                        size="lg"
                      >
                        Confirm Payment
                        <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Activation Section after successful payment */}
        {paidPlanId && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-16"
          >
            <Card className="p-6 bg-green-50 border-green-200">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
                <div className="mb-3 md:mb-0">
                  <h3 className="text-lg font-semibold text-gray-900">Payment received</h3>
                  <p className="text-sm text-gray-700">Transaction: {lastTxnId}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={handleActivate} className="bg-green-600 hover:bg-green-700">Activate Membership</Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Benefits Section */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900">Membership Benefits</h2>
            <p className="text-gray-600 mt-2">
              Enjoy exclusive benefits with our membership plans
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              >
                <Card className="p-6 text-center h-full">
                  <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={benefit.icon} className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </motion.div>
          <div className="max-w-3xl mx-auto">
            <Card className="p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">How do membership plans work?</h3>
                  <p className="mt-2 text-gray-600">
                    Our membership plans allow you to pay a one-time fee and then apply to multiple applications without paying any service charges. You'll only pay the actual application fees required by the institutions.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">Can I upgrade my plan?</h3>
                  <p className="mt-2 text-gray-600">
                    Yes, you can upgrade your plan at any time. When upgrading, you'll pay the difference between your current plan and the new plan.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">What happens if I don't use all my applications?</h3>
                  <p className="mt-2 text-gray-600">
                    Applications included in your membership are valid for the duration of your plan. Any unused applications will expire when your membership ends.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">How do I access the study materials?</h3>
                  <p className="mt-2 text-gray-600">
                    Once your membership is active, you'll get access to our digital library of study materials, previous question papers, and other resources through your account dashboard.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPlans;